import { useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Loader, Text } from '@kula-ai/components';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import { SelectionBar } from '../SelectionBar';
import { SuggestedActionChips } from '../SuggestedActionChips';
import { CandidateCard } from './CandidateCard';
import { CandidateHoverProfile } from './CandidateHoverProfile';
import { fillTemplate } from './candidateCopy';
import { SparkleGradientDef } from './SparkleGradient';
import type { CandidateBlockProps } from './CandidateBlock.types';

export const CandidateBlock = ({ data }: CandidateBlockProps) => {
  const { send, isSending } = useChatSubmit();
  const isStreaming = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return false;
    const tab = s.openTabs.find((t) => t.sessionId === sid);
    return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
  });
  const disabled = isSending || isStreaming;

  const blockKey = data.toolCallId ?? 'no-tool-call-id';
  const selection = useAgentForgeStore((s) => s.selectionByBlock[blockKey]);
  const toggle = useAgentForgeStore((s) => s.toggleBlockSelection);
  const setSelection = useAgentForgeStore((s) => s.setBlockSelection);
  const clearSelection = useAgentForgeStore((s) => s.clearBlockSelection);
  const [showWeak, setShowWeak] = useState(false);

  const allIds = data.candidates.map((c) => c.id);
  const selectedIds = selection ?? [];
  const appIds = data.appIds ?? [];
  const nameById = Object.fromEntries(
    data.candidates.map((c) => [c.id, c.name])
  );

  // Fold the weak tail behind a toggle when strongCount < result count.
  const strongCount = data.strongCount;
  const hasFold =
    typeof strongCount === 'number' && strongCount < data.candidates.length;
  const strongCandidates = hasFold
    ? data.candidates.slice(0, strongCount)
    : data.candidates;
  const weakCandidates = hasFold ? data.candidates.slice(strongCount) : [];
  const strongIds = strongCandidates.map((c) => c.id);
  // Count + select-all track the on-screen cards: strong-only until the weaker
  // matches are expanded, so the header never claims 25 while 15 are folded.
  const visibleIds = showWeak ? allIds : strongIds;

  // Nothing ticked → a bulk action targets the visible strong set (CORE-4970);
  // allIds still rides along so fillTemplate keeps its app-id index aligned.
  const sendFollowup = (template: string) => {
    if (disabled) return;
    const targetIds = selectedIds.length > 0 ? selectedIds : strongIds;
    void send(fillTemplate(template, targetIds, allIds, appIds, nameById));
  };

  // Append a count only when the user has explicitly CHECKED rows — a "(25)"
  // suffix on every chip when nothing is selected read as noise and implied a
  // mass action. Bare labels still act on the visible list (sendFollowup
  // targets the strong set when nothing is checked); the count appears the
  // moment a selection narrows the target.
  const labelFor = (label: string, template: string): string => {
    const multi = template.includes('{ids}') || template.includes('{count}');
    if (!multi || selectedIds.length === 0) return label;
    return `${label} (${selectedIds.length})`;
  };

  // Drop "top N" actions whose N exceeds the results returned — e.g.
  // "Shortlist top 5" when only 2 candidates came back (CORE-4956).
  const visibleActions = (data.suggestedActions ?? []).filter((a) => {
    const m = a.label.match(/\btop\s+(\d+)\b/i);
    return !m || Number(m[1]) <= data.candidates.length;
  });

  const renderCard = (c: (typeof data.candidates)[number]) => (
    <CandidateHoverProfile key={c.id} candidate={c}>
      {(bind) => (
        <CandidateCard
          candidate={c}
          checked={selectedIds.includes(c.id)}
          onToggle={() => toggle(blockKey, c.id)}
          nameTrigger={bind}
        />
      )}
    </CandidateHoverProfile>
  );

  return (
    <>
      <SparkleGradientDef />
      <Box data-test-id="agentforge-candidate-block">
        {/* Nexus's answer / match-warning / applied filters. */}
        {data.answer && (
          <Text
            variant="caption"
            as="p"
            className="text-secondary-800"
            data-test-id="agentforge-candidate-answer"
          >
            {data.answer}
          </Text>
        )}
        {data.matchWarning && (
          <Box
            mt="6px"
            px="10px"
            py="6px"
            borderRadius="8px"
            bg="pastel-6-light"
            color="pastel-6-dark"
            fontSize="12px"
            fontWeight="500"
            data-test-id="agentforge-candidate-match-warning"
          >
            {data.matchWarning === 'no_exact_matches'
              ? 'No exact match for every requirement — showing the closest candidates.'
              : 'No strong match — these are the closest; review carefully before acting.'}
          </Box>
        )}
        {data.appliedFilterChips && data.appliedFilterChips.length > 0 && (
          <Flex wrap="wrap" gap="6px" mt="8px">
            {data.appliedFilterChips.map((chip) => (
              <Box
                key={chip}
                px="8px"
                py="2px"
                borderRadius="6px"
                bg="secondary-50"
                color="secondary-700"
                fontSize="12px"
                fontWeight="500"
              >
                {chip}
              </Box>
            ))}
          </Flex>
        )}
        <Box mt="10px">
          <SelectionBar
            selected={selectedIds.length}
            total={visibleIds.length}
            noun="candidates"
            matchedTotal={data.matchedCount}
            onSelectAll={() => setSelection(blockKey, visibleIds)}
            onClear={() => clearSelection(blockKey)}
            testId="agentforge-candidate-selection"
          />
        </Box>
        <Flex direction="column" gap="10px" mt="10px">
          {strongCandidates.map(renderCard)}
        </Flex>

        {hasFold && (
          <Box mt="10px">
            <Box
              as="button"
              type="button"
              onClick={() => setShowWeak((v) => !v)}
              px="12px"
              py="6px"
              borderRadius="8px"
              border="1px solid"
              borderColor="border-color"
              bg="base-white"
              color="secondary-700"
              fontSize="12px"
              fontWeight="500"
              cursor="pointer"
              _hover={{ bg: 'secondary-50' }}
              data-test-id="agentforge-candidate-toggle-weak"
            >
              {showWeak
                ? 'Hide weaker matches'
                : `Show ${weakCandidates.length} weaker ${
                    weakCandidates.length === 1 ? 'match' : 'matches'
                  }`}
            </Box>
            {showWeak && (
              <Flex direction="column" gap="10px" mt="10px">
                {weakCandidates.map(renderCard)}
              </Flex>
            )}
          </Box>
        )}

        {/* Page more rows from nexus's cached set via kula_search(cache_key, offset). */}
        {data.loadMore && (
          <Box mt="10px">
            <Box
              as="button"
              type="button"
              disabled={disabled}
              opacity={disabled ? 0.5 : 1}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              onClick={() => {
                if (disabled || !data.loadMore) return;
                void send(
                  `Load the next page of the previous search (cache_key: ${data.loadMore.cacheKey}, offset: ${data.loadMore.offset}).`
                );
              }}
              px="12px"
              py="6px"
              borderRadius="8px"
              border="1px solid"
              borderColor="border-color"
              bg="base-white"
              color="secondary-700"
              fontSize="12px"
              fontWeight="500"
              _hover={{ bg: 'secondary-50' }}
              data-test-id="agentforge-candidate-load-more"
            >
              <Flex as="span" align="center" gap="6px">
                {disabled && <Loader size={16} />}
                {typeof data.loadMore.remaining === 'number'
                  ? `Show ${data.loadMore.remaining} more`
                  : 'Show more'}
              </Flex>
            </Box>
          </Box>
        )}

        {visibleActions.length > 0 && (
          <Box mt="14px">
            <SuggestedActionChips
              actions={visibleActions.map((a) => ({
                ...a,
                label: labelFor(a.label, a.promptTemplate),
              }))}
              onSelect={sendFollowup}
              disabled={disabled}
              testIdPrefix="agentforge-candidate-suggested"
            />
          </Box>
        )}
      </Box>
    </>
  );
};
